import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ExternalLink, RefreshCw } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { Skeleton } from '../components/ui/Skeleton';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';

type ActionKind =
  | 'PO_BLOCKED'
  | 'PO_TO_SEND'
  | 'PO_AWAITING_APPROVAL'
  | 'PO_NO_LONGER_NEEDED'
  | 'LINE_REJECTED'
  | 'LINE_NOT_FOUND'
  | 'LINE_LATE'
  | 'ORDER_UNPAID_HELD'
  | 'NOT_COLLECTED'
  | 'LINE_NOT_ORDERED';

type Severity = 'CRITICAL' | 'HIGH' | 'NORMAL';

interface ActionItem {
  kind: ActionKind;
  severity: Severity;
  since: string;
  title: string;
  detail: string;
  orderId?: string | null;
  orderNumber?: string | null;
  customerName?: string | null;
  lineId?: string | null;
  productLabel?: string | null;
  poId?: string | null;
  poNumber?: string | null;
  supplierName?: string | null;
  slipId?: string | null;
  dueAt?: string | null;
}

interface ActionQueueData {
  counts: Record<ActionKind, number>;
  items: ActionItem[];
}

/** The order the sections are offered in — the buyer's first, then the counter's. */
const KINDS: ActionKind[] = [
  'LINE_NOT_ORDERED',
  'PO_BLOCKED',
  'LINE_REJECTED',
  'PO_TO_SEND',
  'PO_AWAITING_APPROVAL',
  'PO_NO_LONGER_NEEDED',
  'LINE_LATE',
  'LINE_NOT_FOUND',
  'ORDER_UNPAID_HELD',
  'NOT_COLLECTED',
];

const SEVERITY_CLASS: Record<Severity, string> = {
  CRITICAL: 'bg-destructive/20 text-destructive',
  HIGH: 'bg-destructive/10 text-destructive',
  NORMAL: 'bg-muted text-foreground',
};

const DATE_FMT = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
});

/** An unparseable date must not take the page down (Intl throws RangeError on Invalid Date). */
function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : DATE_FMT.format(d);
}

/** How long this has been waiting, which is the only number the screen is sorted on. */
function waitingFor(iso: string): string {
  const hours = Math.floor((Date.now() - Date.parse(iso)) / 3_600_000);
  if (hours < 1) return '<1 h';
  if (hours < 48) return `${hours} h`;
  return `${Math.floor(hours / 24)} j`;
}

/**
 * E12 — "action required".
 *
 * Everything here is derived from the orders, the purchase orders and the pickup slips, so there
 * is nothing to tick off: an entry leaves the screen when the thing it describes is dealt with,
 * wherever it was dealt with. That is why the only actions offered are links to the record that
 * can actually be changed.
 */
export default function ActionQueue() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showApiError } = useToast();
  const [params, setParams] = useSearchParams();
  const [queue, setQueue] = useState<ActionQueueData | null>(null);
  const [loading, setLoading] = useState(true);

  const kind = (params.get('kind') as ActionKind | null) ?? null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // The route answers { data: { counts, items } } like every other list endpoint. Reading the
      // body itself as the queue left `counts` undefined and the page crashed on its first render.
      const { data } = await apiClient.get<{ data: ActionQueueData }>(
        `/action-queue${kind ? `?kind=${kind}` : ''}`
      );
      setQueue({
        counts: data.data?.counts ?? ({} as ActionQueueData['counts']),
        items: data.data?.items ?? [],
      });
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    void load();
  }, [load]);

  const total = useMemo(
    () => (queue ? Object.values(queue.counts ?? {}).reduce((a, b) => a + (b ?? 0), 0) : 0),
    [queue]
  );

  const select = (next: ActionKind | null) => {
    if (next) setParams({ kind: next });
    else setParams({});
  };

  /** The record this entry is really about — a PO for the buyer, an order for everyone else. */
  const openRecord = (item: ActionItem) => {
    if (
      item.poId &&
      (item.kind === 'PO_BLOCKED' ||
        item.kind === 'PO_TO_SEND' ||
        item.kind === 'PO_AWAITING_APPROVAL' ||
        item.kind === 'PO_NO_LONGER_NEEDED')
    ) {
      navigate(`/purchasing/orders?po=${item.poId}`);
      return;
    }
    if (item.orderId) navigate(`/orders/${item.orderId}`);
  };

  return (
    <div>
      <PageHeader
        title={t('actionQueue.title')}
        description={t('actionQueue.subtitle')}
        actions={
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('common.refresh')}
          </Button>
        }
      />

      {/* One chip per kind, with its count: the morning glance before the detail. */}
      <div className="flex flex-wrap gap-2 mb-5">
        <button
          type="button"
          onClick={() => select(null)}
          className={`text-xs rounded px-2 py-1 border ${kind === null ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
        >
          {t('actionQueue.all')} ({total})
        </button>
        {KINDS.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => select(k)}
            className={`text-xs rounded px-2 py-1 border ${kind === k ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
          >
            {t(`actionQueue.kinds.${k}`)} ({queue?.counts[k] ?? 0})
          </button>
        ))}
      </div>

      {loading && <Skeleton className="h-40 w-full" />}

      {!loading && queue && queue.items.length === 0 && (
        <div className="rounded border border-dashed p-10 text-center text-sm text-muted-foreground">
          {t('actionQueue.empty')}
        </div>
      )}

      {!loading && queue && queue.items.length > 0 && (
        <ul className="space-y-2">
          {queue.items.map((item) => (
            <li
              key={`${item.kind}-${item.lineId ?? item.poId ?? item.slipId ?? item.orderId}`}
              className="rounded border p-3 flex items-start gap-3"
            >
              <AlertTriangle
                className={`h-4 w-4 mt-0.5 shrink-0 ${item.severity === 'NORMAL' ? 'text-muted-foreground' : 'text-destructive'}`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded ${SEVERITY_CLASS[item.severity] ?? SEVERITY_CLASS.NORMAL}`}
                  >
                    {t(`actionQueue.severity.${item.severity}`)}
                  </span>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                    {t(`actionQueue.kinds.${item.kind}`)}
                  </span>
                  <span className="font-medium truncate">{item.title}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">{item.detail}</p>
                <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-4">
                  <span>{t('actionQueue.waiting', { duration: waitingFor(item.since) })}</span>
                  {item.customerName && <span>{item.customerName}</span>}
                  {item.supplierName && <span>{item.supplierName}</span>}
                  {item.dueAt && <span>{t('actionQueue.due', { when: fmtDate(item.dueAt) })}</span>}
                </div>
              </div>
              {(item.orderId || item.poId) && (
                <Button variant="ghost" onClick={() => openRecord(item)}>
                  <ExternalLink className="h-4 w-4 mr-1" />
                  {item.poNumber ?? item.orderNumber ?? t('common.open')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
